import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../Button';
import { describeError } from '../../services/errorMessage';

/**
 * Rückfrage vor dem Löschen einer Bewerbung — eigener Dialog statt
 * Browser-Dialog (CI-Ratsche), Aufbau wie `KarteErneuernDialog`. Gelöscht
 * werden die Zeile und ihre Anhänge in der Ablage; das lässt sich nicht
 * zurücknehmen. Escape und ein Klick daneben brechen ab, solange nichts läuft.
 */

interface Props {
    name: string;
    anhaenge: number;
    /** Die Löschfrist ist erreicht — dann ist Löschen die Pflicht, nicht die Ausnahme. */
    loeschFaellig: boolean;
    onLoeschen: () => Promise<unknown>;
    onAbbrechen: () => void;
}

export const TalentLoeschenDialog: React.FC<Props> = ({ name, anhaenge, loeschFaellig, onLoeschen, onAbbrechen }) => {
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState('');
    const abbrechenRef = useRef<HTMLButtonElement | null>(null);
    const laeuftRef = useRef(false);

    // Fokus auf „Abbrechen“: Enter allein soll nichts löschen.
    useEffect(() => { abbrechenRef.current?.focus(); }, []);

    useEffect(() => {
        const taste = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || laeuftRef.current) return;
            e.preventDefault();
            e.stopPropagation();
            onAbbrechen();
        };
        document.addEventListener('keydown', taste, true);
        return () => document.removeEventListener('keydown', taste, true);
    }, [onAbbrechen]);

    const loeschen = async () => {
        if (laeuftRef.current) return;
        laeuftRef.current = true;
        setLaeuft(true);
        setFehler('');
        try {
            await onLoeschen();
        } catch (e) {
            setFehler(describeError(e));
        } finally {
            laeuftRef.current = false;
            setLaeuft(false);
        }
    };

    const abbrechen = () => { if (!laeuftRef.current) onAbbrechen(); };
    const mitAnhaengen = anhaenge === 0 ? '' : anhaenge === 1 ? ' und dem Anhang' : ` und ${anhaenge} Anhängen`;

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={abbrechen}>
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="talent-loeschen-titel"
                aria-describedby="talent-loeschen-text"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-full overflow-y-auto border border-slate-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 flex gap-3 items-start">
                    <div className="p-2 rounded-xl shrink-0 bg-red-100 text-red-700">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div className="min-w-0 space-y-2">
                        <h3 id="talent-loeschen-titel" className="font-bold text-lg text-slate-800">Bewerbung löschen?</h3>
                        <div id="talent-loeschen-text" className="text-sm text-slate-600 leading-relaxed space-y-2">
                            <p>
                                Die Bewerbung von „{name}“ wird mit allen Angaben{mitAnhaengen} endgültig gelöscht.
                                Das lässt sich nicht zurücknehmen.
                            </p>
                            {loeschFaellig && (
                                <p className="font-semibold text-slate-800">Die Löschfrist dieser Bewerbung ist erreicht.</p>
                            )}
                        </div>
                        {fehler && <p role="alert" className="text-xs text-red-700">{fehler}</p>}
                    </div>
                </div>
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col-reverse sm:flex-row justify-end gap-2 rounded-b-2xl">
                    <button
                        ref={abbrechenRef}
                        type="button"
                        onClick={abbrechen}
                        disabled={laeuft}
                        className="min-h-[44px] px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                    >
                        Abbrechen
                    </button>
                    <Button variant="danger" className="min-h-[44px]" icon={<Trash2 className="w-4 h-4" />} loading={laeuft} onClick={() => { void loeschen(); }}>
                        Endgültig löschen
                    </Button>
                </div>
            </div>
        </div>
    );
};
